import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Send } from "lucide-react";
import { PageHead } from "@/components/AppLayout";
import { Card, Avatar, EmptyState } from "@/components/ui-kit";
import api from "@/lib/api";

export default function Messages() {
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const bodyRef = useRef(null);

  useEffect(() => {
    api.get("/conversations").then((r) => { setConvos(r.data); if (r.data.length) setActive(r.data[0]); });
  }, []);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [active]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const { data } = await api.post(`/conversations/${active.id}/messages`, { text });
    setActive(data); setConvos(convos.map((c) => (c.id === data.id ? data : c)));
    setText(""); toast.success("Message sent");
  };

  return (
    <>
      <PageHead title="Messages" subtitle="Direct conversations between you and the assigned physiotherapist." />
      {convos.length === 0 ? (
        <Card><EmptyState icon={<MessageSquare size={24} />} title="No conversations yet"
          subtitle="A conversation opens automatically once PranIQ Admin assigns a physiotherapist to one of your treatment orders." /></Card>
      ) : (
        <Card className="grid grid-cols-[300px_1fr] overflow-hidden min-h-[560px]">
          <div className="border-r border-slate-200 overflow-y-auto">
            {convos.map((c) => (
              <button key={c.id} onClick={() => setActive(c)} data-testid={`conversation-${c.id}`}
                className={`w-full flex gap-3 px-4 py-3.5 border-b border-slate-100 text-left ${active?.id === c.id ? "bg-teal-50" : "hover:bg-slate-50"}`}>
                <Avatar name={c.name} size={38} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13px]">{c.name}</div>
                  <div className="text-[12px] text-slate-500 truncate">{c.messages?.length ? c.messages[c.messages.length - 1].text : c.meta}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="flex flex-col">
            {active && (<>
              <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-3">
                <Avatar name={active.name} size={38} />
                <div><div className="font-semibold text-[13.5px]">{active.name}</div><div className="text-[11.5px] text-slate-500">{active.meta}</div></div>
              </div>
              <div ref={bodyRef} className="flex-1 p-5 flex flex-col gap-3 overflow-y-auto max-h-[400px]">
                {(!active.messages || active.messages.length === 0) && <div className="text-center text-sm text-slate-400 my-auto">Start the conversation with {active.name}.</div>}
                {active.messages?.map((m) => (
                  <div key={m.id} className={`max-w-[70%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed ${m.who === "me" ? "bg-teal-600 text-white self-end rounded-br-sm" : "bg-slate-100 self-start rounded-bl-sm"}`}>{m.text}</div>
                ))}
              </div>
              <div className="flex gap-2.5 px-5 py-3.5 border-t border-slate-200">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message..." className="pq-input flex-1" />
                <button className="pq-btn pq-btn-primary" data-testid="message-send-button" onClick={send}><Send size={16} /></button>
              </div>
            </>)}
          </div>
        </Card>
      )}
    </>
  );
}
